/* @refresh reset */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  fetchCompanyAiMailboxProcessingLogs,
  fetchUserPersonalMailAiProcessingLogs,
} from "../../../api/microsoft";
import { summarizeMailboxProcessingAction } from "./mailboxProcessingLogShared";

/**
 * Fetches and renders processing log entries for one Graph message (dev / ops).
 * Mount only when visible (e.g. logs tab) so fetch runs once per open.
 * @param {{ mailboxId?: number, graphMessageId: string, internetMessageId?: string|null, variant?: 'company'|'personal' }} props
 */
export function CompanyAiMailboxProcessingLogList({
  mailboxId,
  graphMessageId,
  internetMessageId,
  variant = "company",
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!graphMessageId) return undefined;
    if (variant !== "personal" && !mailboxId) return undefined;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    const req =
      variant === "personal"
        ? fetchUserPersonalMailAiProcessingLogs({
            graphMessageId,
            internetMessageId,
            limit: 30,
          })
        : fetchCompanyAiMailboxProcessingLogs(mailboxId, {
            graphMessageId,
            internetMessageId,
            limit: 30,
          });
    req
      .then((data) => {
        if (!cancelled) setLogs(data?.processing_logs || []);
      })
      .catch((e) => {
        if (!cancelled) {
          setLogs([]);
          setErr(
            e?.response?.data?.errors?.[0] ||
              t("microsoft.ai_mailboxes.processing_log.load_failed")
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [variant, mailboxId, graphMessageId, internetMessageId, t]);

  if (loading) {
    return <p className="text-sm text-gray-500">{t("microsoft.status.loading")}</p>;
  }
  if (err) {
    return <p className="text-sm text-rose-600">{err}</p>;
  }
  if (logs.length === 0) {
    return (
      <p className="text-sm text-gray-600">{t("microsoft.ai_mailboxes.processing_log.empty")}</p>
    );
  }

  return (
    <ul className="space-y-4">
      {logs.map((log) => (
        <li
          key={log.id}
          className="rounded-lg border border-gray-100 bg-gray-50/80 p-3 text-xs text-gray-800"
        >
          <div className="flex flex-wrap items-center gap-2 border-b border-gray-200/80 pb-2">
            <span className="font-medium text-gray-900">
              {new Date(log.recorded_at).toLocaleString()}
            </span>
            <span className="rounded bg-white px-1.5 py-0.5 font-mono text-[10px] text-gray-600 ring-1 ring-gray-200">
              {log.run_mode}
            </span>
            {log.dry_run ? (
              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-900">
                {t("microsoft.ai_mailboxes.processing_log.dry_run")}
              </span>
            ) : (
              <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-900">
                {t("microsoft.ai_mailboxes.processing_log.live_run")}
              </span>
            )}
            {log.ok === false ? (
              <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-medium text-rose-900">
                {t("microsoft.ai_mailboxes.processing_log.failed")}
              </span>
            ) : (
              <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-700">
                {t("microsoft.ai_mailboxes.processing_log.ok")}
              </span>
            )}
          </div>
          {log.error ? <p className="mt-2 text-rose-700">{log.error}</p> : null}
          {log.ai_category ? (
            <p className="mt-2">
              <span className="font-semibold text-gray-700">
                {t("microsoft.ai_mailboxes.processing_log.ai_category")}:{" "}
              </span>
              {log.ai_category}
              {log.ai_confidence != null && (
                <span className="text-gray-500">
                  {" "}
                  ({t("microsoft.ai_mailboxes.processing_log.confidence")}: {log.ai_confidence}
                  {log.ai_source ? `, ${log.ai_source}` : ""})
                </span>
              )}
            </p>
          ) : null}
          <p className="mt-2 font-medium text-gray-700">
            {t("microsoft.ai_mailboxes.processing_log.actions")}
          </p>
          <ul className="mt-1 list-inside list-disc space-y-0.5 text-gray-700">
            {(log.actions || []).map((a, i) => (
              <li key={i} className="break-words">
                {summarizeMailboxProcessingAction(a, t)}
              </li>
            ))}
          </ul>
          <details className="mt-3">
            <summary className="cursor-pointer text-[11px] font-medium text-violet-700 hover:text-violet-900">
              {t("microsoft.ai_mailboxes.processing_log.raw_json")}
            </summary>
            <pre className="mt-2 max-h-48 overflow-auto rounded border border-gray-200 bg-white p-2 font-mono text-[10px] leading-relaxed text-gray-800">
              {JSON.stringify(log.message_payload || log, null, 2)}
            </pre>
          </details>
        </li>
      ))}
    </ul>
  );
}
